import { Box, BoxProps, forwardRef } from '@bonusly/design-system';

export type BizyHintBubbleProps = BoxProps & {
	/**
	 * Corner treatment:
	 *  - `bottom-left` (default): squares the bottom-left corner into a "tail"
	 *    pointing back at a bee sitting to the lower-left (bubble on bee's right).
	 *  - `bottom-right`: squares the bottom-right corner (bubble on bee's left).
	 *  - `none`: symmetric 12px radius, for bubbles that use a separate `PopoverArrow`
	 *    (give-box accordion).
	 */
	tail?: 'bottom-left' | 'bottom-right' | 'none';
};

const TAIL_RADIUS: Record<NonNullable<BizyHintBubbleProps['tail']>, string> = {
	'bottom-left': '12px 12px 12px 4px',
	'bottom-right': '12px 12px 4px 12px',
	none: '12px',
};

/**
 * The shared visual shell for every Bizy hint/assist bubble (comment box, give-box
 * popout, give-box accordion). It owns the look — surface, hairline border, corner
 * radius/tail, drop shadow, backdrop blur, and the Plus Jakarta display font — and
 * nothing about anchoring, so each surface keeps its own positioning:
 *
 *   - plain positioned box:  `<BizyHintBubble as="button" position="absolute" .../>`
 *   - popover content:       `<BizyHintBubble as={PopoverContent} .../>`
 *
 * Every style prop is overridable (e.g. a surface that needs the Figma border /
 * shadow instead of the defaults just passes `borderColor` / `boxShadow`).
 */
export const BizyHintBubble = forwardRef<BizyHintBubbleProps, 'div'>(({ tail = 'bottom-left', children, ...props }, ref) => (
	<Box
		ref={ref}
		bg="bg.surface"
		border="1px solid"
		borderColor="border.soft"
		borderRadius={TAIL_RADIUS[tail]}
		boxShadow="0 3px 3px 0 rgba(0, 0, 0, 0.08)"
		backdropFilter="blur(2px)"
		fontFamily='"Plus Jakarta Sans", sans-serif'
		textAlign="left"
		px={3}
		py={2}
		{...props}
	>
		{children}
	</Box>
));
